import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Column, Columns, Link, Typography } from "@aviary";
import { removeLocalData } from "@helpers/accessLocalStorage/accessLocalStorage";
import { AuthHeader } from "@shared/components/AuthHeader/AuthHeader";
import { AuthHeaderWrapper } from "@shared/components/AuthHeaderWrapper/AuthHeaderWrapper";
import { CountrySelect } from "@shared/components/CountrySelect/CountrySelect";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useUnauthedExperiment } from "@shared/hooks/useExperiment/useUnauthedExperiment";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useIsRupaRedirectExperience } from "@shared/hooks/useIsRupaRedirectExperience/useIsRupaRedirectExperience";
import { useSonicNPIImprovementsExperiment } from "@shared/hooks/useSonicNPIImprovementsExperiment/useSonicNPIImprovementsExperiment";
import { useSonicSegmentProvidersExperiment } from "@shared/hooks/useSonicSegmentProviderExperiment/useSonicSegmentProviderExperiment";
import { useSearchParams, useLocation } from "@shared/react-router-dom/react-router-dom";
import { SEGMENTATION_QUESTIONS_COMPLETED } from "@shared/types/localStorageKeys";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { SignUpCarouselOrFooter } from "@unauthenticated/shared/components/SignUpCarouselOrFooter/SignUpCarouselOrFooter";
import { SignUpPageRefresh } from "@unauthenticated/shared/components/SimplifySignUp/SignUpPageRefresh/SignUpPageRefresh";
import { SimplifySignUp } from "@unauthenticated/shared/components/SimplifySignUp/SimplifySignUp";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PractitionerSignUpUnauthenticatedSharedMutation as PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation.generated";
import { l } from "@unauthenticated/shared/locales/i18n";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PageContent } from "@unauthenticated/signupSignin/components/PageContent/PageContent";
import PractitionerSignupStepper from "@unauthenticated/signupSignin/components/PractitionerSignupStepper/PractitionerSignupStepper";

import * as styles from "./PractitionerSignUpPage.styles";

interface Props {
  additionalAttributes?: PractitionerAdditionalAttributesType;
  ssoErrors: string;
  referralPartnerName?: string;
}

const PractitionerSignUpPage = ({
  additionalAttributes,
  ssoErrors,
  referralPartnerName,
}: Props) => {
  const { t } = useTranslation();
  const { tablet, desktop } = useBreakpoints();

  useEffect(() => {
    const smartBanner = document.querySelector<HTMLElement>(".smartbanner");
    if (!smartBanner) return;

    const savedMarginTop = document.documentElement.style.marginTop;

    smartBanner.style.display = "none";
    document.documentElement.style.marginTop = "0px";
    document.documentElement.style.setProperty("--smartbanner-height", "0px");

    return () => {
      smartBanner.style.display = "";
      document.documentElement.style.marginTop = savedMarginTop;
      const restoredHeight = smartBanner.offsetHeight;
      document.documentElement.style.setProperty("--smartbanner-height", `${restoredHeight}px`);
    };
  }, []);

  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const step = searchParams.get("step");
  const isMobile = desktop.lessThan;
  const isSonicNPIImprovementsExperiment = useSonicNPIImprovementsExperiment();
  const isRupaRedirectExperience = useIsRupaRedirectExperience();

  const handleOnCompleted = (data: PractitionerSignUpMutationData) => {
    if (isSonicNPIImprovementsExperiment) removeLocalData(SEGMENTATION_QUESTIONS_COMPLETED);
    if (data.auth?.practitionerSignUp?.redirectPath) {
      window.location.assign(data.auth.practitionerSignUp.redirectPath);
    }
  };

  const [isSignUpPageRefreshEnabled] = useFlippers(
    "polaris_sign_up_page_refresh_2023Q3_experiment"
  );
  const isSonicSegmentProvidersEnabled = useSonicSegmentProvidersExperiment();

  const group = useUnauthedExperiment({
    experiment: "signUpRefreshPolaris",
    groups: ["control", "signUpPageRefresh"],
    shouldLog: isSignUpPageRefreshEnabled,
  });

  const isSignUpRefresh =
    isSignUpPageRefreshEnabled && group === "signUpPageRefresh" && !isMobile && step === "1";

  const renderSignupRefresh = () => {
    if (!isSignUpRefresh) return null;

    return (
      <Column columnWidth={3} css={styles.videoContainer} textAlign="center">
        <SignUpPageRefresh />
      </Column>
    );
  };

  const renderHeader = () => {
    return (
      <div css={styles.headerWrapper}>
        <AuthHeader />
        {!!isSonicSegmentProvidersEnabled && step !== null && (
          <div css={styles.stepperMobile}>
            <PractitionerSignupStepper
              hidden={!tablet.lessThan}
              size="small"
              currentStep={Number(step) || 0}
              hideLabels
            />
          </div>
        )}
      </div>
    );
  };

  const renderCountryPickerHeader = () => {
    return (
      <Arrange justify="spaceBetween" alignment="center">
        {!!tablet.greaterThan && (
          <Typography isMarginless>{t(l.common.WhereAreYouLocated)}</Typography>
        )}
        <CountrySelect slug={authRoutes.practitioner_sign_up} />
      </Arrange>
    );
  };

  const renderHeaderAction = () => {
    if (isSonicSegmentProvidersEnabled && step === null && !tablet.lessThan) {
      return renderCountryPickerHeader();
    }

    return renderSignInLink();
  };

  const renderSignInLink = () => {
    if (tablet.lessThan) return null;

    return (
      <Typography isMarginless css={styles.signInLink}>
        {t(l.common.HaveAcount)}{" "}
        <Link isColor="success" to={{ pathname: authRoutes.login, search }}>
          {t(l.common.SignIn)}
        </Link>
      </Typography>
    );
  };

  const renderFooter = () => {
    return <SignUpCarouselOrFooter isWithinViewport={false} hideFooterSignIn />;
  };

  return (
    <Page data-e2e="sign-up-page">
      <Columns data-e2e="sign-up-page-refresh" css={styles.marginRemoved}>
        {renderSignupRefresh()}
        <Column columnWidth={isSignUpRefresh ? 9 : 12} css={styles.mainPageColumn} isPaddingless>
          <AuthHeaderWrapper includeBorder>
            {renderHeader()}
            {!!isSonicSegmentProvidersEnabled && !tablet.lessThan && step !== null && (
              <div css={styles.stepperDiv}>
                <PractitionerSignupStepper
                  hidden={tablet.lessThan}
                  size="large"
                  currentStep={Number(step) || 0}
                />
              </div>
            )}

            {renderHeaderAction()}
          </AuthHeaderWrapper>
          <PageContent
            customStyles={
              isRupaRedirectExperience ? styles.pageContentRupaRedirect : styles.pageContent
            }
          >
            <SimplifySignUp
              onCompleted={handleOnCompleted}
              additionalAttributes={additionalAttributes}
              ssoErrors={ssoErrors}
              referralPartnerName={referralPartnerName}
            />
          </PageContent>
          <div css={isRupaRedirectExperience ? styles.footerNoMargin : undefined}>
            {renderFooter()}
          </div>
        </Column>
      </Columns>
    </Page>
  );
};

export { PractitionerSignUpPage };
